/** Test-render dialog for one notification, standalone.
 *
 *  Owns the detail fetch itself so "Test render" opens ONLY the dialog — it
 *  used to piggyback on the inspector's fetch, which meant the details drawer
 *  had to open behind it. The query key is the inspector's, so opening one
 *  after the other still costs a single request.
 *
 *  `useNotificationDraft` derives its state from a COMPLETE detail payload
 *  (delivery methods, rules, refs, advanced rules, auto-replies), so it is
 *  called in a child that mounts only once the payload has arrived.
 */
import { Alert, CircularProgress, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import * as notificationsApi from '@/api/notifications';

import { TestRenderDialog } from '../notifications/test-render/TestRenderDialog';
import { useStickyRenderContext } from '../notifications/test-render/useStickyRenderContext';
import { useNotificationDraft } from '../notifications/useNotificationDraft';

interface Props {
  notificationId: number;
  onClose: () => void;
}

export function TestRenderForNotification({ notificationId, onClose }: Props) {
  const detail = useQuery({
    // Same key as NodeInspector's — one fetch serves both surfaces.
    queryKey: ['notification-detail', notificationId],
    queryFn: () => notificationsApi.retrieve(notificationId),
    staleTime: 30_000,
  });

  if (detail.data) {
    return <Loaded detail={detail.data} onClose={onClose} />;
  }

  // A placeholder dialog rather than nothing: the click must produce something
  // immediately, or it reads as a dead menu item on a cold cache.
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent>
        {detail.isError ? (
          <Alert severity="error">Couldn’t load this notification.</Alert>
        ) : (
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', py: 1 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Loading notification…
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Loaded({
  detail,
  onClose,
}: {
  detail: notificationsApi.NotificationDetail;
  onClose: () => void;
}) {
  const draft = useNotificationDraft(detail);
  // Same sticky per-notification context as the notification editor's surfaces.
  const [source, setSource] = useStickyRenderContext(detail.id);
  return (
    <TestRenderDialog
      open
      onClose={onClose}
      detail={detail}
      draft={draft}
      source={source}
      onSourceChange={setSource}
    />
  );
}
